import React from "react";
import TextInput from "../common/TextInput";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import {
  DeploymentUpdateDto,
  LocationResponseDto,
  ProjectResponseDto,
  SensorResponseDto,
} from "../common/ObjectTypes";
import DateSelector from "../common/DateSelector";
import SelectInput from "../common/SelectInput";
import { RJSFSchema } from "@rjsf/utils";
import SchemaForm from "../common/SchemaForms.tsx/SchemaForm";
import { Link } from "react-router-dom";
import { LinkIcon } from "../common/ProjectIcons";
import {
  formatDateAsLocalString,
  parseLocalDateStringToDate,
} from "../common/helpers";

interface DeploymentFormProps {
  deployment: DeploymentUpdateDto;
  project: ProjectResponseDto | undefined;
  projectGuid: string;
  deploymentGuid: string;
  deploymentLoading: boolean;
  locations: LocationResponseDto[];
  sensors: SensorResponseDto[];
  // schema: any;
  onClose: () => void;
  onSave: (addAnother: boolean) => void;
  onSelectLocation: (location: LocationResponseDto) => void;
  onSelectSensor: (sensor: SensorResponseDto) => void;
  errors: any;
  rjsfErrors: any;
  onChange: any;
  saving: boolean;
  savingAndAddAnother: boolean;
  existingDeployment: boolean;
  canModify: boolean;
  saveDisabled: boolean;
  canUploadImages: boolean;
  /**
   * Opens the CSV import (AKL-815). Supplied by the parent only when the user may CREATE
   * deployments; the button is gated on this prop, not on `canModify`, because editing
   * (EDIT_DEPLOYMENTS_LOCATIONS) is a different permission. Only offered on the Add page —
   * an existing deployment has nothing to import.
   */
  onImportFromCsv?: () => void;
}

const DeploymentForm: React.FC<DeploymentFormProps> = ({
  deployment,
  project,
  projectGuid,
  deploymentGuid,
  deploymentLoading,
  locations,
  sensors,
  // schema,
  onClose,
  onSave,
  onSelectLocation,
  onSelectSensor,
  onChange,
  errors,
  rjsfErrors,
  saving,
  savingAndAddAnother,
  existingDeployment,
  canModify,
  saveDisabled,
  canUploadImages,
  onImportFromCsv,
}) => {
  const { t } = useTranslation();
  const maxDeploymentNameLength = 255;
  /**
   * Reads a stored "YYYY-MM-DD" day as that same day at local midnight, which
   * is what the date picker shows and compares against. Never reads it through
   * UTC: that showed the previous day to anyone ahead of UTC (AKL-828).
   * @param date_string The stored day, or null/"" when unset.
   * @returns The day as a local Date, or null when unset or unparseable.
   */
  const toPickerDate = (date_string: string | null): Date | null => {
    if (date_string === null || date_string === "") return null;
    const date = parseLocalDateStringToDate(date_string.slice(0, 10));
    if (date === null || Number.isNaN(date.getTime())) return null;
    return date;
  };

  /**
   * Hands the picked day to the parent as a "YYYY-MM-DD" string built from the
   * Date's local year, month and day, so the form state only ever holds
   * date-only strings. A cleared picker becomes "".
   * @param e The picker's synthetic change event: the field name and the Date.
   */
  const handleDateChange = (e: {
    target: { name: string; value: Date | null };
  }) => {
    onChange({
      target: {
        name: e.target.name,
        value: formatDateAsLocalString(e.target.value),
      },
    });
  };

  const defaultLocation = locations.find(
    (location: any) => location.location_id === deployment.location_id,
  );

  const defaultSensor = sensors.find(
    (sensor: SensorResponseDto) => sensor.sensor_id === deployment.sensor_id,
  );

  const shouldShowSchemaForm =
    project?.deployment_schema &&
    Object.keys(project?.deployment_schema).length > 0;

  // One condition both disables the Name box and greys it. Greying it for every saved
  // deployment made it look locked to users who could still edit it (AKL-1084).
  const nameLocked = existingDeployment && !canModify;

  return (
    <div className="w-full ">
      <div className="flex flex-row ">
        <form
          className="px-8 pt-6 pb-8 mb-4 w-full"
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Editing an existing deployment needs no heading — the populated
              form makes it obvious. The add case keeps its title. */}
          {!existingDeployment && (
            <div className={tailwindClassnames.pageHeaderRow}>
              <h2 className={tailwindClassnames.pageTitle}>
                {t("Add Deployment")}
              </h2>
              {/* type="button" is load-bearing: this sits inside the page <form>, and a
                  default (submit) button would fire its onSubmit. Disabled while a save
                  runs so an import cannot start on top of it. */}
              {onImportFromCsv && (
                <button
                  type="button"
                  onClick={onImportFromCsv}
                  disabled={saving || savingAndAddAnother}
                  className={tailwindClassnames.btnSecondary}
                  data-testid="import-deployments-from-csv"
                >
                  {t("Import from CSV")}
                </button>
              )}
            </div>
          )}

          {deploymentLoading ? (
            <div>
              <BeatLoader
                color={tailwindClassnames.skyBlue}
                loading={true}
                size={10}
                margin={5}
              />
            </div>
          ) : (
            <div>
              <div
                className={`${tailwindClassnames.formSectionStack} text-left mb-6`}
              >
                <div
                  className={`${tailwindClassnames.formSectionCard} flex flex-wrap`}
                >
                  <div
                    className={tailwindClassnames.detailsPageTextInputContainer}
                  >
                    <TextInput
                      name="deployment_name"
                      label={`${t("Name")}*`}
                      placeholder=""
                      value={deployment.deployment_name || ""}
                      disabled={nameLocked}
                      className={
                        nameLocked
                          ? tailwindClassnames.detailsPageTextInputDisabled
                          : tailwindClassnames.detailsPageTextInput
                      }
                      limit={maxDeploymentNameLength}
                      type="text"
                      onChange={onChange}
                      error={errors.deployment_name}
                    />
                  </div>
                  <div
                    className={tailwindClassnames.detailsPageTextInputContainer}
                  >
                    <SelectInput
                      options={sensors.map((sensor: any) => ({
                        value: sensor.sensor_id,
                        label: sensor.sensor_name,
                      }))}
                      defaultValue={
                        defaultSensor
                          ? {
                              value: defaultSensor.sensor_id,
                              label: defaultSensor.sensor_name,
                            }
                          : undefined
                      }
                      value={
                        defaultSensor
                          ? {
                              value: defaultSensor.sensor_id,
                              label: defaultSensor.sensor_name,
                            }
                          : undefined
                      }
                      className={"w-full"}
                      isDisabled={existingDeployment && !canModify}
                      errors={errors.sensor_id}
                      isLoading={false}
                      isClearable={true}
                      isSearchable={true}
                      isMulti={false}
                      onChange={onSelectSensor}
                      name={t("Sensor")}
                      searchBar={false}
                    />
                  </div>

                  <div
                    className={tailwindClassnames.detailsPageTextInputContainer}
                  >
                    <DateSelector
                      label={`${t("Start Date")}`}
                      name="start_date"
                      date={toPickerDate(deployment.start_date)}
                      onDateChange={handleDateChange}
                      maxDate={toPickerDate(deployment.end_date) ?? undefined}
                      disabled={existingDeployment && !canModify}
                    />
                  </div>
                  <div
                    className={tailwindClassnames.detailsPageTextInputContainer}
                  >
                    <DateSelector
                      label={`${t("End Date")}`}
                      name="end_date"
                      date={toPickerDate(deployment.end_date)}
                      excludeDatesInterval={
                        deployment.start_date === null ||
                        deployment.start_date === ""
                          ? undefined
                          : [
                              {
                                start: new Date(0),
                                end:
                                  toPickerDate(deployment.start_date) ??
                                  new Date(0),
                              },
                            ]
                      }
                      onDateChange={handleDateChange}
                      disabled={existingDeployment && !canModify}
                    />
                  </div>
                  <div
                    className={tailwindClassnames.detailsPageTextInputContainer}
                  >
                    <SelectInput
                      options={locations.map((location: any) => ({
                        value: location.location_id,
                        label: location.location_name,
                      }))}
                      defaultValue={
                        defaultLocation
                          ? {
                              value: defaultLocation.location_id,
                              label: defaultLocation.location_name,
                            }
                          : undefined
                      }
                      className={"w-full"}
                      isDisabled={existingDeployment && !canModify}
                      errors={errors.country_id}
                      isLoading={false}
                      isClearable={true}
                      isSearchable={true}
                      isMulti={false}
                      onChange={onSelectLocation}
                      name={t("Location")}
                      searchBar={false}
                    />
                  </div>
                </div>
                {shouldShowSchemaForm && (
                  <div
                    className={`${tailwindClassnames.formSectionCard} flex flex-wrap`}
                  >
                    <SchemaForm
                      schema={(project?.deployment_schema as RJSFSchema) || {}}
                      ui_Schema={project?.ui_schema || {}}
                      formdata={deployment.schema_data || {}}
                      onChange={onChange}
                      errors={rjsfErrors}
                      readOnly={!canModify}
                    />
                  </div>
                )}
                {/* <div className={tailwindClassnames.formSection}> */}
                {/* <div className={"w-full mt-4 ml-2"}>
                      <TextInput
                        name="comments"
                        label={t("Comments")}
                        placeholder=""
                        value={deployment.comments || ""}
                        error=""
                        disabled={!canModify}
                        className={
                          canModify
                            ? tailwindClassnames.detailsPageTextInput
                            : tailwindClassnames.detailsPageTextInputDisabled
                        }
                        type="text"
                        initialFocus={false}
                        onChange={onChange}
                        limit={2000}
                        rows={2}
                      />
                    </div> */}
                {/* </div> */}
                {/* <div className={tailwindClassnames.formSection}>
                    <div className="flex flex-col">
                      <label>CUSTOM FORMS</label>
                      <div className="flex flex-row items-center mt-2">
                        <button
                          type="button"
                          onClick={() => handleClickCustomFormButton()}
                          className={tailwindClassnames.addButton}
                        >
                          {t("Add Custom Form")}
                        </button>
                        <Tooltip
                          title={t("Add a custom form to the deployment.")}
                          placement="right"
                          arrow
                          slotProps={{ tooltip: { sx: { fontSize: "1em" } } }}
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 600 }}
                        ><div><InfoIcon /></div></Tooltip></div>
                    </div>
                  </div> */}
              </div>

              {existingDeployment && canUploadImages && deploymentGuid && (
                <div className="mt-2 mb-2 text-left">
                  <Link
                    to={`/manage/${projectGuid}/${deploymentGuid}`}
                    className="text-sm text-storm-blue dark:text-sky-blue hover:underline inline-flex items-center gap-1"
                  >
                    {t("Upload/Import files")}
                    <LinkIcon />
                  </Link>
                </div>
              )}
              <div className="flex flex-row justify-end items-center gap-2.5 mt-3">
                <button
                  type="button"
                  onClick={onClose}
                  className={tailwindClassnames.btnSecondary}
                >
                  {canModify && !saveDisabled ? t("Cancel") : t("Close")}
                </button>
                {!existingDeployment && (
                  <button
                    type="button"
                    onClick={() => onSave(true)}
                    className={`relative ${tailwindClassnames.btnSecondary}`}
                    disabled={saving || savingAndAddAnother || saveDisabled}
                  >
                    {/* Label stays in layout while saving so the button keeps
                        its width; currentColor spinner reads on the fill in
                        both themes. */}
                    <span className={savingAndAddAnother ? "invisible" : ""}>
                      {t("Save and Add Another")}
                    </span>
                    {savingAndAddAnother && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <BeatLoader
                          color="currentColor"
                          loading={true}
                          size={9}
                        />
                      </span>
                    )}
                  </button>
                )}
                {canModify && (
                  <button
                    type="button"
                    onClick={() => onSave(false)}
                    disabled={saving || saveDisabled || savingAndAddAnother}
                    className={`relative ${tailwindClassnames.btnPrimary}`}
                  >
                    <span className={saving ? "invisible" : ""}>
                      {t("Save")}
                    </span>
                    {saving && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <BeatLoader
                          color="currentColor"
                          loading={true}
                          size={9}
                        />
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default DeploymentForm;
